/**
 * Mantine theme matching the existing Hawfinch app.
 *
 * Values were lifted verbatim from the Alpine app this replaced, rather than
 * approximated, so the two apps sit side by side without a visible seam during
 * the migration. That file describes itself as a "ClickHouse-style light theme:
 * dark sidebar + solid black buttons", and the shape of it is:
 *
 *   - light by default; a soft dark scheme is opt-in (values in tokens.css)
 *   - near-black (#161517) as the accent; primary buttons are solid black in
 *     light, and off-white with dark text in dark
 *   - a dark sidebar against the page, in both schemes
 *   - Inter throughout
 *
 * Defining it once here is also the point: the old app hand-writes colours per
 * element, and its light and dark values drifted far enough apart to need three
 * consecutive commits (1b76a22, ff4d313, 41ceaab) fixing invisible text and
 * leftovers. Everything below derives from one definition.
 */
import {
  Checkbox, Indicator, Pagination, Radio, Title, alpha, createTheme, defaultVariantColorsResolver,
  parseThemeColor, type CSSVariablesResolver, type MantineColorsTuple, type MantineTheme,
  type VariantColorsResolver,
} from '@mantine/core';

/**
 * Colour tokens, as CSS variable references. The values live in tokens.css,
 * one block per scheme, so every `TOKENS.x` follows light and dark without
 * the component knowing. Exported so components stop inventing values.
 *
 * A token is a `var()`, not a colour, so never do string maths on one (no
 * alpha suffix, no concatenation into another colour). Add a token instead.
 */
export const TOKENS = {
  bg: 'var(--rs-bg)',
  surface: 'var(--rs-surface)',
  surface2: 'var(--rs-surface2)',
  /** Anything that floats over the page: menus, popovers, modals, tooltips. */
  surface3: 'var(--rs-surface3)',
  surfaceDeep: 'var(--rs-surface-deep)',
  border: 'var(--rs-border)',
  borderStrong: 'var(--rs-border-strong)',
  /** Input, checkbox and select outlines. Clears 3:1, where a hairline does not. */
  borderControl: 'var(--rs-border-control)',
  text: 'var(--rs-text)',
  textSecondary: 'var(--rs-text-secondary)',
  textMuted: 'var(--rs-text-muted)',
  accent: 'var(--rs-accent)',
  /** Text on an accent fill. */
  onAccent: 'var(--rs-on-accent)',
  positive: 'var(--rs-positive)',
  negative: 'var(--rs-negative)',
  /*
    The marketing site's single colour. It is the only hue in an otherwise
    monochrome product: live indicators, revenue, sold, "up". `green` is for
    marks (dots, lines, bars); as text on white it is only ~2.6:1, so words
    in green use `greenText`, which clears 4.5:1 in both schemes.
  */
  green: 'var(--rs-green)',
  greenText: 'var(--rs-green-text)',
  greenSoft: 'var(--rs-green-soft)',
  red: 'var(--rs-red)',
  redText: 'var(--rs-red-text)',
  redSoft: 'var(--rs-red-soft)',
  amberText: 'var(--rs-amber-text)',
  // Dark sidebar, in both schemes
  sidebar: 'var(--rs-sidebar)',
  sidebarBorder: 'var(--rs-sidebar-border)',
  sidebarText: 'var(--rs-sidebar-text)',
  sidebarTextBright: 'var(--rs-sidebar-text-bright)',
  sidebarMuted: 'var(--rs-sidebar-muted)',
  sidebarLabel: 'var(--rs-sidebar-label)',
  sidebarActiveBg: 'var(--rs-sidebar-active-bg)',
  sidebarActiveText: 'var(--rs-sidebar-active-text)',
  // Depth: barely there in light, dropped or deepened in dark.
  shadowCard: 'var(--rs-shadow-card)',
  shadowChip: 'var(--rs-shadow-chip)',
  shadowPop: 'var(--rs-shadow-pop)',
  /** A pale band behind whatever the pointer is over, e.g. a bar chart's cursor. */
  hoverTint: 'var(--rs-hover-tint)',
} as const;

/**
 * What an email looks like to the person who gets it: white, whatever this
 * app's scheme. For the editor canvas, the HTML preview and the starter
 * thumbnails, which all show the recipient's view. Literal colours rather
 * than tokens, because they must not follow the scheme, and an iframe's
 * srcdoc cannot read this page's variables anyway.
 */
export const EMAIL_PAPER = {
  bg: '#ffffff',
  text: '#161517',
  muted: '#6b6c71',
} as const;

/**
 * The site's three faces, and what each is for:
 *
 *   - display: Funnel Display 800, for the big numbers a screen exists to show
 *   - mono:    Geist Mono, for small uppercase labels and figures in a column
 *   - body:    Inter, for everything else
 *
 * Loaded from @fontsource in main.tsx (bundled, so the Tauri build works
 * offline). Each stack falls back to a face that is already on the machine.
 */
export const FONTS = {
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  display: "'Funnel Display', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

/**
 * Type presets as plain style objects, so a component can spread one onto a
 * Mantine `style` prop or a raw element alike. Sizes are deliberately modest:
 * the app is dense, and the site's 34px display figures would crowd a tile.
 */
export const TYPE = {
  /** A headline figure. Pair with a fontSize; 28-32px for a KPI tile. */
  display: {
    fontFamily: FONTS.display,
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '-0.01em',
    fontVariantNumeric: 'tabular-nums',
  },
  /** The small uppercase label above a figure: "TOTAL LEADS". */
  monoLabel: {
    fontFamily: FONTS.mono,
    fontWeight: 500,
    fontSize: 11,
    lineHeight: 1.4,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: TOKENS.textMuted,
  },
  /** Figures in a list or legend, where columns of digits must line up. */
  monoFigure: {
    fontFamily: FONTS.mono,
    fontWeight: 500,
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
  },
} as const;

/**
 * Near-black accent. Shade 6 is #161517, which filled buttons use in light.
 * Dark uses shade 1, the off-white text colour, and `autoContrast` puts dark
 * text on it.
 */
const ink: MantineColorsTuple = [
  '#f6f6f7', '#e8e8ea', '#d9d9db', '#b8b8bb', '#8a8a8d',
  '#5c5c5f', '#161517', '#121113', '#0d0c0e', '#080709',
];

// Data colours, taken from the semantic values already used in base.css so
// charts here read as the same product rather than a different one.
const chartBlue: MantineColorsTuple = [
  '#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa',
  '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a',
];
const chartGreen: MantineColorsTuple = [
  '#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80',
  '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d',
];
const chartRed: MantineColorsTuple = [
  '#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171',
  '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d',
];
const chartAmber: MantineColorsTuple = [
  '#fffbeb', '#fef3c7', '#fde68a', '#fcd34d', '#fbbf24',
  '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f',
];
const chartViolet: MantineColorsTuple = [
  '#f5f3ff', '#ede9fe', '#ddd6fe', '#c4b5fd', '#a78bfa',
  '#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95',
];
const chartCyan: MantineColorsTuple = [
  '#ecfeff', '#cffafe', '#a5f3fc', '#67e8f9', '#22d3ee',
  '#06b6d4', '#0891b2', '#0e7490', '#155e75', '#164e63',
];

/**
 * Categorical order for multi-series charts. Assigned in this fixed order and
 * never cycled, so a colour follows the entity rather than its rank.
 *
 * The order is not cosmetic. It is the one that passes a colour-blindness
 * check on every ADJACENT pair, which is what a reader actually has to tell
 * apart. Two earlier values failed:
 *
 *   - `ink.4` (#8a8a8d) had almost no chroma, so it read as "no data" grey
 *     rather than as a series. Replaced with cyan.
 *   - `chartAmber.6` was #b45309, a brown that sat 7.3 deltaE from green under
 *     deuteranopia. Re-stepped to #d97706 and moved away from green.
 *
 * Verify with the palette validator, don't eyeball it:
 *   node scripts/validate_palette.js "#2563eb,#d97706,#16a34a,#7c3aed,#dc2626,#0891b2" --mode light
 *
 * Amber/green still sit in the 6-8 deltaE band under protanopia, which is only
 * legal alongside a second, non-colour cue. Every categorical chart here has
 * one (the donut is directly labelled), so do not drop those labels.
 *
 * Light uses shade 6 of each. Dark steps to shade 4 (#60a5fa, #fbbf24 and so
 * on), which clears 3:1 on the dark surface where shade 6 would sink into it.
 * The values are --rs-series-1..6 in tokens.css, and theme.contrast.test.ts
 * checks both sets.
 */
export const CHART_SERIES = [
  'var(--rs-series-1)', 'var(--rs-series-2)', 'var(--rs-series-3)',
  'var(--rs-series-4)', 'var(--rs-series-5)', 'var(--rs-series-6)',
] as const;

/**
 * Mantine's dark palette, index 0 to 9, set to the soft greys in tokens.css.
 * Mantine reads dark[0] for text, dark[2] for dimmed, dark[4] for borders,
 * dark[6] for inputs and default buttons and dark[7] for Paper, so these line
 * up with the tokens: text, muted, hairline, surface2 and surface.
 */
const dark: MantineColorsTuple = [
  '#e8e8ea', '#b4b5ba', '#a3a5ac', '#7a7c84', '#484950',
  '#393a41', '#313238', '#2a2b30', '#212226', '#1b1c1f',
];

/**
 * Mantine paints the page, Paper and the app shell from --mantine-color-body,
 * which would put the dark page on dark[7], the card colour. It is pointed at
 * the page ground instead, and tokens.css lifts cards and floating surfaces
 * back onto their own steps.
 *
 * `dimmed` in light is the refined muted grey. Mantine's own gray.6 is 3.3:1
 * on white, under the 4.5:1 that text needs.
 *
 * `primaryShade.dark` is 1 so the primary button is off-white in dark. Mantine
 * applies that shade to every colour, though, which would turn a red button
 * or a green badge pale pink and mint. So every hue is put back on Mantine's
 * own dark steps (filled 8, light 6 at 15%, text 3). The ink and the dark
 * greys keep shade 1, which is what makes a `dark` avatar off-white on a
 * faint tint rather than grey on grey.
 */
export const cssVariablesResolver: CSSVariablesResolver = (t) => ({
  variables: {},
  light: {
    '--mantine-color-body': 'var(--rs-bg)',
    '--mantine-color-dimmed': 'var(--rs-text-muted)',
  },
  dark: {
    '--mantine-color-body': 'var(--rs-bg)',
    ...Object.fromEntries(
      Object.keys(t.colors)
        .filter((name) => name !== t.primaryColor && name !== 'dark')
        .flatMap((name) => darkColourSteps(t, name)),
    ),
  },
});

function darkColourSteps(t: MantineTheme, name: string): [string, string][] {
  const c = t.colors[name];
  if (!c) return [];
  const v = (shade: number) => `var(--mantine-color-${name}-${shade})`;
  return [
    [`--mantine-color-${name}-filled`, v(8)],
    [`--mantine-color-${name}-filled-hover`, v(9)],
    [`--mantine-color-${name}-light`, alpha(c[6], 0.15)],
    [`--mantine-color-${name}-light-hover`, alpha(c[6], 0.2)],
    [`--mantine-color-${name}-light-color`, v(3)],
    [`--mantine-color-${name}-outline`, v(4)],
    [`--mantine-color-${name}-outline-hover`, alpha(c[4], 0.05)],
    [`--mantine-color-${name}-text`, v(4)],
  ];
}

/*
  Mantine works out a filled colour's text (black or white) once, in JS, from
  the light shade, so in dark the off-white primary would still get white
  text. The primary's text is pointed at --mantine-primary-color-contrast
  instead, which Mantine sets per scheme. Other colours keep their filled
  shade dark in both schemes, so their white text stays right.
*/
const isPrimary = (t: MantineTheme, color: string | undefined) => {
  if (!color) return true;
  const parsed = parseThemeColor({ color, theme: t });
  return parsed.color === t.primaryColor && parsed.shade === undefined;
};
const PRIMARY_CONTRAST = 'var(--mantine-primary-color-contrast)';

const variantColorResolver: VariantColorsResolver = (input) => {
  const out = defaultVariantColorsResolver(input);
  if (input.variant === 'filled' && isPrimary(input.theme, input.color)) {
    return { ...out, color: PRIMARY_CONTRAST };
  }
  return out;
};

export const theme = createTheme({
  primaryColor: 'ink',
  primaryShade: { light: 6, dark: 1 },
  // A filled colour gets black or white text by its own lightness, so the
  // off-white primary in dark gets dark text without a per-button override.
  autoContrast: true,
  colors: { ink, dark, chartBlue, chartGreen, chartRed, chartAmber, chartViolet, chartCyan },
  // Real colours, not tokens: Mantine works out contrast and alpha from them.
  white: '#ffffff',
  black: '#161517',

  fontFamily: FONTS.body,
  fontFamilyMonospace: FONTS.mono,
  headings: {
    fontFamily: FONTS.body,
    fontWeight: '600',
    /*
      h2 is the page title on every screen. It matches the dashboard's
      PageHeader: 24px with the site's tight tracking (set on Title below),
      which is most of what separates a heading from a label at this size.
    */
    sizes: { h2: { fontSize: '24px', lineHeight: '1.2' } },
  },
  // base.css --fs-1 .. --fs-8
  fontSizes: {
    xs: '0.6875rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '1rem',
    xl: '1.125rem',
  },
  lineHeights: { xs: '1.25', sm: '1.4', md: '1.55', lg: '1.55', xl: '1.55' },
  // base.css --radius-sm / --radius / --radius-lg
  radius: { sm: '6px', md: '10px', lg: '14px' },
  defaultRadius: 'md',
  // base.css --sp-1 .. --sp-7
  spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' },

  components: {
    Title: Title.extend({
      styles: (_theme, props) => (props.order === 2
        ? { root: { letterSpacing: '-0.02em', color: TOKENS.text } }
        : {}),
    }),
    /*
      The site's .mockup surface, scaled down: white, a hairline border, a
      12px corner and a shadow you notice only when it is missing. Padding
      stays at `lg` so no existing layout moves; cards that set `p={0}` for a
      flush table still get the corner, because Card clips its children.
    */
    Card: {
      defaultProps: { withBorder: true, padding: 'lg', radius: 12 },
      styles: {
        root: {
          borderColor: TOKENS.border,
          backgroundColor: TOKENS.surface,
          boxShadow: TOKENS.shadowCard,
        },
      },
    },
    Table: {
      defaultProps: { highlightOnHover: true, verticalSpacing: 'sm', fz: 'sm' },
      /*
        Header treatment taken from the old app's table, measured rather than
        guessed: 12px, weight 700, 0.6px tracking, uppercase, muted. Mantine's
        default is 11px sentence-case in near-black, which reads as body text
        and makes a data grid look like a list. Set on the theme so every table
        in the app gets it, not just the one screen that was compared.
      */
      styles: {
        th: {
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: 'var(--mantine-color-dimmed)',
        },
      },
    },
    Button: { defaultProps: { radius: 'md' } },
    Modal: {
      defaultProps: { radius: 'md', overlayProps: { backgroundOpacity: 0.4, blur: 2 } },
    },
    Badge: { defaultProps: { radius: 'sm' } },
    Alert: { defaultProps: { radius: 'md' } },
    // The tick, page number and count on a primary fill: see variantColorResolver.
    Checkbox: Checkbox.extend({
      vars: (t, props) => (isPrimary(t, props.color) && !props.iconColor
        ? { root: { '--checkbox-icon-color': PRIMARY_CONTRAST } }
        : { root: {} }),
    }),
    Radio: Radio.extend({
      vars: (t, props) => (isPrimary(t, props.color) && !props.iconColor
        ? { root: { '--radio-icon-color': PRIMARY_CONTRAST } }
        : { root: {} }),
    }),
    Pagination: Pagination.extend({
      vars: (t, props) => (isPrimary(t, props.color)
        ? { root: { '--pagination-active-color': PRIMARY_CONTRAST } }
        : { root: {} }),
    }),
    Indicator: Indicator.extend({
      vars: (t, props) => (isPrimary(t, props.color)
        ? { root: { '--indicator-text-color': PRIMARY_CONTRAST } }
        : { root: {} }),
    }),
  },
  variantColorResolver,
});

/** Money, in pounds. One implementation so no screen invents its own. */
export function formatGBP(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-GB').format(value);
}

/** ClickHouse hands back "2026-08-28 10:05:33.244"; render it in UK style. */
export function formatDateTime(raw: string): string {
  if (!raw) return '';
  const iso = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw;
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}
